// IMPORT REACT
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ReactQuill, { Quill } from "react-quill";
import { components } from "react-select";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { SimpleAsyncSelect } from "../../../components/CustomFields/CustomSelect";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { PreferencesContext } from "../../../PreferenceContext";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import { PAGE_SIZE } from "../../../services/common/const";
import { GetQuickNotesList } from "../../../services/QuickNotesService";
import QuickNotesSave from "../../QuickNotes/QuickNotesSave";
import { GetInstructionList } from "../../../services/InstructionService";
import {
  CreateHistoryTemplate,
  GetHistoryTemplateDetail,
  UpdateHistoryTemplate,
} from "../../../services/HistoryTemplateService";
import { insertTextAtCursor } from "../../../shared/CommonFunction";
import parse from "html-react-parser";

//SCHEMA OF HISTORY TEMPLATE
interface IHistoryTemplate {
  historyTemplateId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  title: string;
  note: string;
  isDeleted: boolean;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  consumerId: string;
  orgId: string;
  branchId: string;
  departmentId: string;
  specialityId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  specialityId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  consumerId: "",
  orgId: "",
  branchId: "",
  departmentId: "",
};

const skeletonRowCount = 5;

export default function HistoryTemplateSave(Props: any) {
  // PROPS
  const { isForPopUp = false, handleClosePopUp, name, templateId } = Props;

  // DEFAULT SCHEMA VALUE FOR FORM
  const inintHistoryTemplate: IHistoryTemplate = {
    historyTemplateId: 0,
    consumerId: 0,
    orgId: 0,
    branchId: 0,
    title: isForPopUp ? name ?? "" : "",
    note: "",
    isDeleted: false,
  };

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawHistoryTemplateId = urlParams.get("q");
  let historyTemplateId = isForPopUp ? templateId ?? 0 : 0;
  if (
    curRawHistoryTemplateId !== null &&
    curRawHistoryTemplateId !== undefined &&
    isForPopUp === false
  ) {
    let decId = edService.decrypt(curRawHistoryTemplateId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      historyTemplateId = +decId;
    }
  } else {
    if (urlParams.size > 0 && isForPopUp === false) {
      navigate("/not-found");
    }
  }

  const theme = useTheme();
  const smd = useMediaQuery(theme.breakpoints.down("sm"));

  // STATES FOR DATA STORAGE  -- PLEASE USE PROPER TYPES FOR STATE
  const [param, setParam] = useState(parameters);
  const [asyncSelectKey, setAsyncSelectKey] = React.useState({
    historyReasonSearch: 1,
    QuickNoteSearch: 1,
    historyInstruction: 1,
  });
  const [historyReasonColl, setHistoryReasonColl] = useState<any[]>([]);
  const [quickNoteColl, setQuickNoteColl] = useState<any[]>([]);
  const [instructionColl, setInstructionColl] = useState<any[]>([]);
  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
  });

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    historyTemplateId: Yup.number().required(),
    consumerId: Yup.number().required(),
    orgId: Yup.number().required(),
    branchId: Yup.number().required(),
    title: Yup.string().required("Title is required."),
    isNote: Yup.boolean().nullable(),
    note: Yup.string().nullable(),
    historyTemplateDetailColl: Yup.array()
      .of(
        Yup.object().shape({
          historyTemplateDetailId: Yup.number().required(),
          historyTemplateId: Yup.number().required(),
          displayOrder: Yup.number().required(),
          instructionId: Yup.number().required("Instruction is required."),
          instructionData: Yup.string().required(),
          isDeleted: Yup.boolean().required(),
          mode: Yup.string().nullable(),
        })
      )
      .nullable(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: inintHistoryTemplate, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  //GET LIST OF QUICK NOTE
  const fetchQuickNotesList = async (searchValue: string, oninit: boolean) => {
    try {
      let params = new URLSearchParams();
      let quickNotesColl = [];
      params.append("ConsumerId", userContext?.consumerId?.toString());
      params.append("OrganizationId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("QuickNotesTypeCode", "PATIENT_HISTORY_PAST_HISTORY_NOTE");
      params.append("Mode", "DROPDOWN");
      const result: any = await GetQuickNotesList(params);
      if (result?.data?.data?.quickNotesColl) {
        quickNotesColl = result?.data?.data?.quickNotesColl;
        if (oninit) setQuickNoteColl(quickNotesColl || []);
      }
      return quickNotesColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FILTER QUICKNOTE
  async function filterQuickNote(inputValue: string) {
    let result = await fetchQuickNotesList(inputValue, false);
    return result;
  }

  // QUICK NOTE LOAD ON SEARCH
  const loadQuickNoteList = (
    inputValue: string,
    callback: (options: any) => void
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterQuickNote(inputValue.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET FOLOWUP TEMPLATE DETAIL
  const getHistoryTemplateDetailById = async () => {
    let result: any;
    let params = new URLSearchParams();
    params.append("historyTemplateId", historyTemplateId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("organizationId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    try {
      const res: any = await GetHistoryTemplateDetail(params);
      result = res.data.data;
      methods.reset({
        ...inintHistoryTemplate,
        historyTemplateId: result?.historyTemplateId || 0,
        title: result?.title || "",
        note: result.note ?? "",
      });
      return result;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE EXAMINATION TEMPLATE
  const saveHistoryTemplate = async (value: IHistoryTemplate) => {
    try {
      let result: any = "";
      let formData = {
        historyTemplateId: value.historyTemplateId,
        consumerId: userContext?.consumerId,
        organizationId: userContext?.organizationId,
        branchId: userContext?.branchId,
        title: value?.title || "",
        note: value?.note || "",
        isDeleted: value.isDeleted,
        dataManagedBy: userContext.loginDetail.userId,
      };
      if (historyTemplateId > 0) {
        result = await UpdateHistoryTemplate(formData);
      } else {
        result = await CreateHistoryTemplate(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          handleClosePopUp(
            historyTemplateId > 0
              ? "HISTORY_TEMPLATE_EDIT"
              : "HISTORY_TEMPLATE_CREATE",
            result?.data
          );
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = (data: any) => {
    saveHistoryTemplate(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      await fetchQuickNotesList("", true);
      if (historyTemplateId > 0) {
        await getHistoryTemplateDetailById();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  const pastHistoryQuillRef = useRef<ReactQuill | null>(null);

  // COMPONENTS
  const quicknotesOptionComponent = (props) => {
    return (
      <components.Option {...props}>
        {props.data.__isNew__ && <Box>{props.children}</Box>}
        {props.data && !props.data.__isNew__ && (
          <Box
            dangerouslySetInnerHTML={{
              __html: props?.children,
            }}
          />
        )}
      </components.Option>
    );
  };

  const handleClickOpenCreatable = (
    value: boolean,
    label: string,
    code: string
  ) => {
    setCustomPopUp({
      ...customPopUp,
      open: value,
      name: label,
      code: code,
    });
  };

  const handleManagePopup = async (code: string, option: any) => {
    setCustomPopUp({ ...customPopUp, open: false });
    setIsLoading(true);
    if (code === "QUICK_NOTES_CREATE") {
      await fetchQuickNotesList("", true);
      setAsyncSelectKey({
        ...asyncSelectKey,
        QuickNoteSearch: asyncSelectKey.QuickNoteSearch + 1,
      });
    }
    setIsLoading(false);
  };

  const handleCloseCreatable = () => {
    setCustomPopUp({ ...customPopUp, open: false, name: "", code: "" });
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [historyTemplateId]);

  return (
    <Box>
      <Box className={"main-container"}>
        {!isForPopUp && (
          <Grid container className="page-header">
            <Grid item xs={6} sm={6} md={6}>
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  alignItems: "center",
                }}>
                <CustomBreadcrumb />
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="outlined"
                  type="button"
                  className="secondary-button"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}>
                  Back
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} mb={3} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.title
                            ? "validation-message"
                            : ""
                        }`}>
                        Template Name
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input">
                        <CustomTextField
                          placeholder="Ex. History for ...."
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          id="title"
                          size="small"
                          autoFocus
                          {...methods.register("title")}
                          error={!!methods.formState.errors.title}
                          className={`text-input ${
                            methods.formState.errors.title ? "error" : ""
                          }`}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.title?.message}
                      </Typography>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Box>
                      <FormControl fullWidth>
                        <Box>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  backgroundColor: "#ffffff",
                                }}>
                                {quickNoteColl?.map((item, index) => (
                                  <Box
                                    className="quick-chip"
                                    key={index}
                                    label={parse(item?.quickNotesName)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      insertTextAtCursor(
                                        pastHistoryQuillRef,
                                        item?.quickNotesName
                                      );
                                    }}>
                                    {parse(item?.quickNotesName)}
                                  </Box>
                                ))}
                                <Grid xs={12}>
                                  <Box
                                    style={{
                                      paddingBottom: "8px",
                                      width: "100%",
                                    }}>
                                    <FormControl fullWidth>
                                      <SimpleAsyncSelect
                                        placeholder="Search or create Quick Note"
                                        id={"QuickNoteSearch"}
                                        key={asyncSelectKey?.QuickNoteSearch?.toString()}
                                        menuPortalTarget={document.body}
                                        handleChange={(e: any) => {
                                          if (e) {
                                            insertTextAtCursor(
                                              pastHistoryQuillRef,
                                              e?.label
                                            );
                                          }
                                        }}
                                        value={null}
                                        onCreateOption={(
                                          inputValue: string
                                        ) => {
                                          handleClickOpenCreatable(
                                            true,
                                            inputValue,
                                            "QUICKNOTE"
                                          );
                                        }}
                                        loadOptions={loadQuickNoteList}
                                        defaultOptions={quickNoteColl}
                                        isSearchable
                                        CustomOptionComponent={
                                          quicknotesOptionComponent
                                        }
                                        FieldName={""}
                                      />
                                    </FormControl>
                                  </Box>
                                </Grid>
                              </Box>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-box">
                              <Controller
                                control={methods.control}
                                name={"note"}
                                render={({ field }) => (
                                  <ReactQuill
                                    {...field}
                                    ref={pastHistoryQuillRef}
                                    theme="snow"
                                    value={methods?.getValues()?.note}
                                    className={`react-quill-alignment`}
                                  />
                                )}
                              />
                            </SkeletonWrapper>
                          </Grid>
                        </Box>
                      </FormControl>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={isLoading || methods.formState.isSubmitting}>
                    {historyTemplateId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    disabled={isLoading || methods.formState.isSubmitting}
                    onClick={handleBack}>
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
          <Dialog
            className="dialogPage"
            open={customPopUp.open && customPopUp.code === "QUICKNOTE"}
            onClose={(e, reason) => {
              if (reason !== "backdropClick") {
                handleCloseCreatable();
              }
            }}
            fullScreen={smd}>
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <Typography
                  style={{ fontWeight: 600, marginLeft: 0 }}
                  sx={{ ml: 2, flex: 1 }}
                  variant="h6">
                  Save Quick Note
                </Typography>
              </Toolbar>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </AppBar>
            <DialogContent sx={{ padding: "12px 24px" }}>
              <QuickNotesSave
                handleClosePopUp={(code: string, company: any) => {
                  handleManagePopup(code, company);
                }}
                isForPopUp={customPopUp.open}
                name={customPopUp.name}
                quickNoteType="history"
              />
            </DialogContent>

            <Divider />
          </Dialog>
        </FormProvider>
      </Box>
    </Box>
  );
}
